import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword } from '../../auth/staffAuthApi';
import { app } from '../../auth/appAuth';
import { useAuth } from '../../context/AuthContext';
import { sendCustomVerificationEmail } from '../../auth/api/emailVerificationApi';
import { acceptCustomPlanInvite, getCustomPlanByToken } from '../services/customPlanApi';
import { createSubscription } from '../../website/services/payment/subscriptionService';
import { getDeluxeEnvironment } from '../../website/services/payment/deluxeEnvironment';
import AuthLoadingState from '../../components/loading/AuthLoadingState';
import CustomPlanInviteAcceptView from '../components/CustomPlanInviteAcceptView';
import {
  formatBillingLabel,
  getCustomPlanTotals,
} from '../utils/customPlanPricing';
import '../styles/custom-plan-invite.css';

const EMPTY_BILLING = {
  street: '',
  city: '',
  state: '',
  zipCode: '',
};

export default function CustomPlanInviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = searchParams.get('token') || '';

  const [plan, setPlan] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [billingAddress, setBillingAddress] = useState(EMPTY_BILLING);
  const [payment, setPayment] = useState(null);
  const [pwScore, setPwScore] = useState(0);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingState, setLoadingState] = useState('initial');
  const [error, setError] = useState('');
  const [allowTestCards] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing invitation token');
      return;
    }

    const load = async () => {
      try {
        const data = await getCustomPlanByToken(token);
        setPlan(data);
        setEmail(data.email || '');
        if (data.customerLabel) {
          setCompanyName(data.customerLabel);
        }
      } catch (err) {
        setError(err?.message || 'Invalid invitation');
      }
    };

    load();
  }, [token]);

  useEffect(() => {
    const value = password || '';
    let score = 0;
    if (value.length >= 6) score += 1;
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score += 1;
    if (/\d/.test(value)) score += 1;
    if (/[^A-Za-z0-9]/.test(value) || value.length >= 12) score += 1;
    setPwScore(score);
  }, [password]);

  const totals = useMemo(() => getCustomPlanTotals(plan?.terms), [plan]);
  const billingLabel = formatBillingLabel(plan?.terms?.billingCycle || 'monthly');

  const handleBillingChange = (event) => {
    const { name, value } = event.target;
    setBillingAddress((prev) => ({ ...prev, [name]: value }));
  };

  const handleAccept = async () => {
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!companyName.trim()) {
      setError('Company name is required.');
      return;
    }
    if (!payment?.customerId || !payment?.paymentMethodId) {
      setError('Please save your card in the Deluxe form.');
      return;
    }
    if (!billingAddress.street || !billingAddress.city || !billingAddress.state || !billingAddress.zipCode) {
      setError('Please complete your billing address.');
      return;
    }

    try {
      setLoading(true);
      setLoadingState('verifying');
      setError('');
      const auth = getAuth(app);

      if (user && user.email && email && user.email.toLowerCase() !== email.toLowerCase()) {
        setError('You are signed in as a different user. Please sign out and use the invited email.');
        setLoadingState('error');
        return;
      }

      setLoadingState('loading');
      const customerName = `${firstName} ${lastName}`.trim();
      const billingCycle = plan.terms.billingCycle === 'annually' ? 'annually' : 'monthly';
      const { grandTotal } = totals;

      const subscriptionResponse = await createSubscription({
        name: plan.terms.planName || 'Custom',
        amount: grandTotal.toFixed(2),
        billingCycle,
        vault: payment,
        metadata: {
          email,
          packageId: 'custom',
          packageName: plan.terms.planName || 'Custom',
          companyName: companyName.trim(),
          customerName,
          billingAddress: { firstName, lastName, ...billingAddress },
          customerId: payment.customerId,
          paymentMethodId: payment.paymentMethodId,
          last4: payment.last4,
          deluxeEnvironment: getDeluxeEnvironment(),
          customPlanId: plan.planId,
        },
        startDate: new Date().toISOString(),
      });

      if (
        !subscriptionResponse?.success
        || !subscriptionResponse?.subscription?.id
        || !subscriptionResponse?.firstPayment?.transactionId
      ) {
        throw new Error(
          subscriptionResponse?.message || 'Payment was not confirmed. No account was created.'
        );
      }

      let currentUser = auth.currentUser;
      if (!currentUser) {
        try {
          const created = await createUserWithEmailAndPassword(auth, email, password);
          currentUser = created.user;
        } catch (createErr) {
          if (createErr?.code === 'auth/email-already-in-use') {
            const signIn = await signInWithEmailAndPassword(auth, email, password);
            currentUser = signIn.user;
          } else {
            throw createErr;
          }
        }
      }

      if (!currentUser || (currentUser.email && email && currentUser.email.toLowerCase() !== email.toLowerCase())) {
        setError('Authenticated user does not match the invited email.');
        setLoadingState('error');
        return;
      }

      const idToken = await currentUser.getIdToken();
      const subscriptionPayload = {
        id: subscriptionResponse.subscription.id,
        status: subscriptionResponse.subscription.status,
        billingCycle: subscriptionResponse.subscription.billingCycle || billingCycle,
        amount: subscriptionResponse.subscription.amount || grandTotal.toFixed(2),
        startDate: subscriptionResponse.subscription.startDate,
        nextBillingDate: subscriptionResponse.subscription.nextBillingDate,
        deluxeCustomerId: payment.customerId,
        deluxePaymentMethodId: payment.paymentMethodId,
        paymentMethodLast4: payment.last4 || subscriptionResponse.subscription?.paymentMethodLast4,
        deluxeEnvironment: getDeluxeEnvironment(),
        gateway: 'deluxe',
        firstPaymentTransactionId: subscriptionResponse.firstPayment.transactionId,
      };

      void sendCustomVerificationEmail(idToken).catch((verifyErr) => {
        console.warn('Could not send verification email:', verifyErr);
      });

      await acceptCustomPlanInvite({
        token,
        idToken,
        firstName,
        lastName,
        companyName: companyName.trim(),
        subscription: subscriptionPayload,
      });

      setLoadingState('finalizing');
      window.location.replace('/app/overview');
    } catch (err) {
      const declined = err?.details?.code === 'CARD_DECLINED'
        || (typeof err?.message === 'string' && err.message.toLowerCase().includes('declined'));
      setError(declined
        ? 'Your card was declined. No account was created.'
        : (err?.message || 'Failed to accept invitation'));
      setLoadingState('error');
    } finally {
      setLoading(false);
    }
  };

  const resetLoadingState = () => {
    setLoading(false);
    setLoadingState('initial');
    setError('');
  };

  const pwLabel = ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'][pwScore];
  const pwClass = ['weak', 'weak', 'fair', 'good', 'strong'][pwScore];

  if (loading) {
    return (
      <div className="cpi-page">
        <div className="cpi-loading-card">
          <AuthLoadingState
            state={loadingState}
            onReset={resetLoadingState}
            customMessage={
              loadingState === 'verifying' ? 'Creating your account...' :
              loadingState === 'loading' ? 'Setting up billing...' :
              loadingState === 'finalizing' ? 'Almost there...' :
              loadingState === 'error' ? 'Setup failed' :
              'Processing...'
            }
            customSubMessage={
              loadingState === 'error' ? (error || 'There was a problem completing setup') :
              'Please wait...'
            }
          />
        </div>
      </div>
    );
  }

  if (!plan && error) {
    return (
      <div className="cpi-page">
        <div className="cpi-state-card">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="cpi-page">
        <div className="cpi-state-card">
          <p>Loading your invitation...</p>
        </div>
      </div>
    );
  }

  return (
    <CustomPlanInviteAcceptView
      plan={plan}
      totals={totals}
      billingLabel={billingLabel}
      email={email}
      firstName={firstName}
      lastName={lastName}
      companyName={companyName}
      password={password}
      confirmPassword={confirmPassword}
      billingAddress={billingAddress}
      showPassword={showPw}
      showConfirmPassword={showPw2}
      passwordScore={pwScore}
      passwordLabel={pwLabel}
      passwordStrengthClass={pwClass}
      error={error}
      allowTestCards={allowTestCards}
      onFirstNameChange={setFirstName}
      onLastNameChange={setLastName}
      onCompanyNameChange={setCompanyName}
      onPasswordChange={setPassword}
      onConfirmPasswordChange={setConfirmPassword}
      onTogglePassword={() => setShowPw((value) => !value)}
      onToggleConfirmPassword={() => setShowPw2((value) => !value)}
      onBillingChange={handleBillingChange}
      onPaymentChange={setPayment}
      payment={payment}
      onAccept={handleAccept}
      onCancel={() => navigate('/app/login')}
    />
  );
}
